import { useState } from "react";
import { useQuery } from "react-query";
import { message } from "antd";
import useAuth from "../../../../../core/hooks/useAuth";
import { useMemo } from "react";
import TeamSkillValidationService from "./TeamSkillValidationService";

const useTeamSkillValidation = () => {
  const { authenticatedUser } = useAuth();
  const [loadingAction, setLoadingAction] = useState(false);

  const {
    data: skillsRequests,
    isLoading,
    error,
    refetch,
  } = useQuery(
    "skillsRequests",
    () => TeamSkillValidationService.getSkillsRequests(),
    { staleTime: 1000 * 60 * 5 }
  );
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredRequests = useMemo(() => {
    if (!skillsRequests || !authenticatedUser?.department) return [];
    const departmentRequests = skillsRequests.filter(
      (req) => req.user?.department === authenticatedUser.department
    );

    return statusFilter === "All"
      ? departmentRequests
      : departmentRequests.filter((req) => req.status === statusFilter);
  }, [skillsRequests, statusFilter, authenticatedUser?.department]);

  const approveSkillRequest = async (requestId) => {
    if (!authenticatedUser?.id) {
      return message.error("User not authenticated!");
    }

    setLoadingAction(true);
    try {
      await TeamSkillValidationService.approveSkillRequest(
        requestId,
        authenticatedUser.id
      );
      message.success("Skill request approved successfully!");
      refetch();
    } catch (error) {
      console.error("❌ API Error:", error.response?.data || error);
      message.error(
        error.response?.data?.message || "Failed to approve skill request."
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const rejectSkillRequest = async (requestId) => {
    if (!authenticatedUser?.id) {
      return message.error("User not authenticated!");
    }

    setLoadingAction(true);
    try {
      await TeamSkillValidationService.rejectSkillRequest(
        requestId,
        authenticatedUser.id
      );
      message.success("Skill request rejected successfully!");
      refetch();
    } catch (error) {
      console.error("❌ API Error:", error.response?.data || error);
      message.error(
        error.response?.data?.message || "Failed to reject skill request."
      );
    } finally {
      setLoadingAction(false);
    }
  };

  return {
    skillsRequests: filteredRequests,
    isLoading,
    error,
    approveSkillRequest,
    rejectSkillRequest,
    loadingAction,
    statusFilter,
    setStatusFilter,
  };
};

export default useTeamSkillValidation;
